import * as React from 'react';
import { Box, GDSStyleObject } from '@leagueplatform/genesis-core';
import { useLocation } from 'react-router-dom';
import { ImpersonationTopBanner } from '@leagueplatform/web-common-components';
import { AppNavBarComponent } from 'components/header-nav/Nav';
import { isMobileUserAgent } from 'utils/is-mobile-user-agent';
import { FooterComponent } from './footer/footer.component';

type BasePageProps = React.PropsWithChildren<{
  css?: GDSStyleObject;
  isPrivatePage?: boolean;
}>;

const CLAIMS_PAGE_ICON_COLOR_STYLES = {
  color: '$primaryBackgroundDefault',
  filter:
    'invert(36%) sepia(94%) saturate(750%) hue-rotate(-14deg) brightness(90%) contrast(85%)',
};

export const BasePage: React.FC<BasePageProps> = ({
  children,
  isPrivatePage = true,
  css,
}) => {
  const location = useLocation();
  const isMobileDevice = isMobileUserAgent();

  const isGetCarePage = location.pathname === '/get-care';
  const isChangePCPPage = location.pathname === '/change-pcp';
  const isProviderRedirect = location.pathname === '/provider-redirect';
  const isClaimsPage = location.pathname === '/claims';
  const isContactInfo = location.pathname === '/contact_info';
  const isHealthJourneyExcluded =
    location.pathname.includes('/health-journey/activity/') ||
    location.pathname.includes('/health-journey/campaign/');
  // Pages mounted under HealthJourneyRoutes share an SDK form layout that
  // ships without bottom spacing on its inputs; bump it via Genesis input
  // wrapper.
  const isHealthJourneyArea =
    location.pathname.startsWith('/health-journey') ||
    location.pathname.startsWith('/health-programs') ||
    location.pathname.startsWith('/health-progress') ||
    location.pathname.startsWith('/health-journey-history');

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        overflowX: 'hidden',
        '& [data-testid="img-icon"], & [data-testid="svg-icon"]': isClaimsPage
          ? CLAIMS_PAGE_ICON_COLOR_STYLES
          : {},
        '& .user-dropdown-item_link:focus::before': {
          boxShadow: 'none',
        },
        '.GDS-button': {
          borderRadius: '99999px',
          whiteSpace: 'nowrap',
          padding: '12px 10px',
        },
        ...css,
      }}
    >
      {isPrivatePage &&
        !isProviderRedirect &&
        !isContactInfo &&
        !isMobileDevice && (
          <Box
            css={{
              flexShrink: 0,
              'a:focus::before': {
                boxShadow: 'none',
              },
            }}
          >
            <ImpersonationTopBanner />
            <AppNavBarComponent />
          </Box>
        )}
      <Box
        as="main"
        css={{
          flex: '1 0 auto',
          position: 'relative',
          overflowX: 'hidden',
          a: isGetCarePage ? { borderRadius: '99999px' } : undefined,
          strong: {
            color: 'inherit !important',
          },
          '& .GDS-input-wrapper': isHealthJourneyArea
            ? { marginBottom: '12px' }
            : undefined,
          ...css,
        }}
      >
        {children}
      </Box>
      {isPrivatePage &&
        !isChangePCPPage &&
        !isProviderRedirect &&
        !isHealthJourneyExcluded &&
        !isContactInfo && (
          <Box css={{ flexShrink: 0 }}>
            <FooterComponent />
          </Box>
        )}
    </Box>
  );
};
